import type { Message, User, Channel } from 'stream-chat';
import { getChatState } from '../store/chatStore';
import type { ChannelType, CustomChannelData } from '../types/chat.types';

export interface NotificationOptions {
  title: string;
  body: string;
  channelId?: string;
  channelName?: string;
  sender?: User;
  message?: Message;
  avatar?: string;
  timestamp?: Date;
}

export interface ToastNotificationOptions extends NotificationOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface SoundNotificationOptions {
  type?: 'message' | 'mention' | 'dispatch-driver';
  volume?: number;
}

export interface BrowserNotificationOptions extends NotificationOptions {
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
  onClick?: () => void;
}

export type NotificationCallback = (options: ToastNotificationOptions) => void;

export interface MessageNotificationContext {
  channel: Channel;
  channelType?: ChannelType;
  channelRegion?: string;
  regionId?: number;
  isPriority?: boolean;
  isCritical?: boolean;
}

class NotificationService {
  private static instance: NotificationService;
  private toastCallback: NotificationCallback | null = null;
  private audioContext: AudioContext | null = null;
  private notificationSounds: Map<string, AudioBuffer> = new Map();

  private constructor() {
    this.initializeAudio();
    this.requestBrowserPermission();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  public setToastCallback(callback: NotificationCallback): void {
    this.toastCallback = callback;
  }

  private async initializeAudio(): Promise<void> {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      await this.preloadSounds();
    } catch (error) {
      console.warn('[NotificationService] Audio context not supported:', error);
    }
  }

  private async preloadSounds(): Promise<void> {
    if (!this.audioContext) return;

    // Generate simple notification sounds using Web Audio API
    const soundConfigs = {
      message: { frequency: 800, duration: 0.2, type: 'sine' as OscillatorType },
      mention: { frequency: 1000, duration: 0.3, type: 'square' as OscillatorType },
      'dispatch-driver': { frequency: 600, duration: 0.4, type: 'triangle' as OscillatorType },
    };

    for (const [type, config] of Object.entries(soundConfigs)) {
      try {
        const audioBuffer = this.generateNotificationSound(
          config.frequency,
          config.duration,
          config.type
        );
        this.notificationSounds.set(type, audioBuffer);
      } catch (error) {
        console.warn(`[NotificationService] Failed to generate ${type} sound:`, error);
      }
    }
  }

  private generateNotificationSound(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine'
  ): AudioBuffer {
    if (!this.audioContext) {
      throw new Error('Audio context not available');
    }

    const sampleRate = this.audioContext.sampleRate;
    const frameCount = sampleRate * duration;
    const audioBuffer = this.audioContext.createBuffer(1, frameCount, sampleRate);
    const channelData = audioBuffer.getChannelData(0);

    // Generate a simple tone with envelope
    for (let i = 0; i < frameCount; i++) {
      const t = i / sampleRate;
      const envelope = Math.exp(-t * 3); // Exponential decay
      
      let sample = 0;
      switch (type) {
        case 'sine':
          sample = Math.sin(2 * Math.PI * frequency * t);
          break;
        case 'square':
          sample = Math.sin(2 * Math.PI * frequency * t) > 0 ? 1 : -1;
          break;
        case 'triangle':
          sample = (2 / Math.PI) * Math.asin(Math.sin(2 * Math.PI * frequency * t));
          break;
        default:
          sample = Math.sin(2 * Math.PI * frequency * t);
      }
      
      channelData[i] = sample * envelope * 0.3; // Keep volume reasonable
    }

    return audioBuffer;
  }

  private async requestBrowserPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (error) {
        console.warn('[NotificationService] Browser notification permission denied:', error);
      }
    }
  }

  public shouldNotifyForMessage(
    message: Message,
    channelId: string,
    context?: MessageNotificationContext
  ): boolean {
    const chatState = getChatState();
    const notificationPrefs = chatState.notificationPreferences;

    // Don't notify if notifications are globally disabled
    if (!notificationPrefs.enabled || !chatState.preferences.notificationsEnabled) {
      return false;
    }

    if (message.user?.id === chatState.currentUser?.id) {
      return false;
    }

    // Extra safety check: Also check case-insensitive match
    if (message.user?.id && chatState.currentUser?.id) {
      const messageUserId = String(message.user.id).toLowerCase();
      const currentUserId = String(chatState.currentUser.id).toLowerCase();

      if (messageUserId === currentUserId) {
        return false;
      }
    }


    // Don't notify if user is currently viewing this channel and page is visible
    if (chatState.activeChannelId === channelId && !document.hidden) {
      return false;
    }

    // Check if message is critical/priority and overrides are enabled
    if (context?.isCritical && notificationPrefs.priorityOverrides) {
      return true; // Always notify for critical messages
    }

    // Check quiet hours
    if (this.isQuietHours(notificationPrefs.quietHours)) {
      // Only allow critical messages during quiet hours
      if (!context?.isCritical || !notificationPrefs.priorityOverrides) {
        return false;
      }
    }

    // Check channel type preferences
    if (context?.channelType && !notificationPrefs.channelTypes[context.channelType]) {
      return false;
    }

    // Check regional filtering
    if (notificationPrefs.respectRegionFilter) {
      const allowRegional = this.shouldNotifyForRegion(context, chatState);
      return allowRegional;
    }

    return true;
  }

  private isQuietHours(quietHours: any): boolean {
    if (!quietHours.enabled) {
      return false;
    }

    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Handle cases where quiet hours span midnight
    if (quietHours.start > quietHours.end) {
      // e.g., 22:00 to 08:00 (crosses midnight)
      return currentTime >= quietHours.start || currentTime <= quietHours.end;
    } else {
      // e.g., 12:00 to 14:00 (same day)
      return currentTime >= quietHours.start && currentTime <= quietHours.end;
    }
  }

  private shouldNotifyForRegion(
    context: MessageNotificationContext | undefined,
    chatState: ReturnType<typeof getChatState>
  ): boolean {
    // If no region filter is set, notify for all regions
    const selectedRegionIds = chatState.filters.regionIds;
    if (!selectedRegionIds || selectedRegionIds.length === 0) {
      return true;
    }

    // If no context provided, allow notification (fallback)
    if (!context) {
      return true;
    }

    // Check if channel's region matches selected regions
    if (context.regionId) {
      const allowed = selectedRegionIds.includes(context.regionId);
      return allowed;
    }

    // Fallback: try to extract region from channel data
    if (context.channel?.data) {
      const channelData = context.channel.data as CustomChannelData;

      // All channels use the same region_id field
      if (channelData.region_id) {
        const allowed = selectedRegionIds.includes(channelData.region_id);
        return allowed;
      }
    }

    // If regional filtering is enabled but we can't determine region, block notification
    return false;
  }

  public async showToastNotification(options: ToastNotificationOptions): Promise<void> {
    if (!this.toastCallback) {
      console.warn('[NotificationService] Toast callback not registered');
      return;
    }

    // Don't show in-app toasts when the user is already on the chat page —
    // they can see updates in the channel list in real-time.
    if (window.location.pathname.includes('/chat') && !document.hidden) {
      return;
    }

    try {
      this.toastCallback(options);
    } catch (error) {
      console.error('[NotificationService] Failed to show toast notification:', error);
    }
  }

  public async playSound(options: SoundNotificationOptions = {}): Promise<void> {
    const chatState = getChatState();
    const soundPrefs = chatState.notificationPreferences.soundPreferences;

    if (!soundPrefs.enabled || !chatState.preferences.soundEnabled || !this.audioContext || !this.notificationSounds.size) {
      return;
    }

    try {
      const soundType = options.type || 'message';
      const audioBuffer = this.notificationSounds.get(soundType);
      
      if (!audioBuffer) {
        console.warn(`[NotificationService] Sound '${soundType}' not found`);
        return;
      }

      // Resume audio context if suspended (required by browser policies)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();

      source.buffer = audioBuffer;
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Set volume (use user preference as base, allow message-specific override)
      const baseVolume = soundPrefs.volume;
      const messageVolume = options.volume ?? 1.0;
      gainNode.gain.value = Math.min(1.0, Math.max(0.0, baseVolume * messageVolume));

      source.start();
    } catch (error) {
      console.error('[NotificationService] Failed to play notification sound:', error);
    }
  }

  public async showBrowserNotification(options: BrowserNotificationOptions): Promise<void> {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(options.title, {
          body: options.body,
          icon: options.icon || '/logo.png',
          tag: options.tag || `chat-${options.channelId}`,
          requireInteraction: options.requireInteraction || false,
        });

        if (options.onClick) {
          notification.onclick = () => {
            options.onClick!();
            notification.close();
          };
        }

        // Auto-close after 5 seconds if not requiring interaction
        if (!options.requireInteraction) {
          setTimeout(() => {
            notification.close();
          }, 5000);
        }
      } catch (error) {
        console.error('[NotificationService] Failed to show browser notification:', error);
      }
    }
  }

  public async notifyNewMessage(
    message: Message,
    channelId: string,
    channelName?: string,
    channelType?: ChannelType,
    context?: Partial<MessageNotificationContext>
  ): Promise<void> {
    // Build complete context for notification decision
    const notificationContext: MessageNotificationContext = {
      channel: context?.channel as Channel,
      channelType,
      channelRegion: context?.channelRegion,
      regionId: context?.regionId,
      isPriority: context?.isPriority || this.isMessagePriority(message),
      isCritical: context?.isCritical || this.isMessageCritical(message),
    };

    if (!this.shouldNotifyForMessage(message, channelId, notificationContext)) {
      return;
    }

    const senderName = message.user?.name || message.user?.id || 'Unknown User';
    const messagePreview = this.getMessagePreview(message);
    const isDispatchDriver = channelType === 'dispatch-driver';
    const notificationTitle = isDispatchDriver ? senderName : `${senderName} in ${channelName || 'Chat'}`;

    // Show toast notification
    await this.showToastNotification({
      title: notificationTitle,
      body: messagePreview,
      channelId,
      channelName,
      sender: (message.user as any) || undefined,
      message,
      avatar: (message.user as any)?.image,
      timestamp: (message as any).created_at ? new Date((message as any).created_at) : new Date(),
      duration: 5000,
      action: {
        label: 'Reply',
        onClick: () => this.navigateToChannel(channelId),
      },
    });

    // Play sound
    await this.playSound({
      type: isDispatchDriver ? 'dispatch-driver' : 'message',
    });

    // Show browser notification (if page is hidden or user prefers it)
    if (document.hidden) {
      await this.showBrowserNotification({
        title: notificationTitle,
        body: messagePreview,
        channelId,
        channelName,
        sender: (message.user as any) || undefined,
        message,
        icon: (message.user as any)?.image || '/logo.png',
        tag: `chat-${channelId}`,
        onClick: () => this.navigateToChannel(channelId),
      });
    }
  }

  private getMessagePreview(message: Message): string {
    // Prefer English translation if available (for admin portal consistency)
    const messageText = (message as any).i18n?.en_text || message.text;

    if (messageText) {
      return messageText.length > 100
        ? `${messageText.substring(0, 97)}...`
        : messageText;
    }

    if (message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      if (attachment.type === 'image') {
        return '📷 Sent an image';
      }
      if (attachment.type === 'file') {
        return `📎 Sent a file: ${attachment.title || 'file'}`;
      }
    }

    return 'New message';
  }

  private isMessagePriority(message: Message): boolean {
    if (!message.text) return false;

    // Check for priority keywords
    const priorityKeywords = [
      'urgent', 'emergency', 'asap', 'priority', 'important',
      'critical', 'help', 'issue', 'problem', 'delay',
      'accident', 'breakdown', 'stuck', 'lost'
    ];

    const messageText = message.text.toLowerCase();
    return priorityKeywords.some(keyword => messageText.includes(keyword));
  }

  private isMessageCritical(message: Message): boolean {
    if (!message.text) return false;

    // Check for critical keywords (subset of priority)
    const criticalKeywords = [
      'emergency', 'critical', 'accident', 'help',
      'breakdown', '911', 'urgent help', 'stuck'
    ];

    const messageText = message.text.toLowerCase();
    return criticalKeywords.some(keyword => messageText.includes(keyword));
  }

  private navigateToChannel(channelId: string): void {
    // Focus window if it's in background
    if (document.hidden) {
      window.focus();
    }

    // Navigate to chat page and select channel
    // This will be handled by the navigation action in the toast callback
    const chatState = getChatState();
    chatState.setActiveChannel(channelId);
    
    // Navigate to chat hub if not already there
    if (!window.location.pathname.includes('/chat')) {
      window.location.href = '/chat';
    }
  }

  public async testNotification(): Promise<void> {
    await this.showToastNotification({
      title: 'Test Notification',
      body: 'This is a test notification to verify the system is working correctly.',
      duration: 3000,
    });

    await this.playSound({ type: 'message' });
  }

  public cleanup(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.notificationSounds.clear();
    this.toastCallback = null;
  }
}

export const notificationService = NotificationService.getInstance();